<template>
  <!-- Rent Information -->
  <BlockContainer class="tenancy-rent-info p-4">
    <ValidationObserver ref="rentInfoForm" v-slot="{ invalid, validated, dirty }">
      <div class="row align-items-center">
        <div class="col">
          <h5>Betalingsinformasjon</h5>
        </div>

        <div class="col edit-buttons text-right">
          <div v-if="editMode">
            <CancelButton class="mr-1" @click.native="editMode = false" />
            <SaveButton
              @click.native="saveAndCloseEdit"
              :loading="loading.post"
              :disabled="(!dirty && !validated) || invalid || loading.post"
              class="ml-1"
            />
          </div>

          <EditButton
            v-else-if="getTenancy.subscription.subscriptionPlanName !== 'Garantert' || isUserSudo"
            @click.native="setEditModeAndLocalCopy"
            :class="{ sudo: getTenancy.subscription.subscriptionPlanName === 'Garantert' && isUserSudo }"
          />
        </div>
      </div>

      <div v-if="getTenancy" class="mt-4">
        <List>
          <ListHeader>
            <div class="col">Produkt</div>
            <div class="col percentage">MVA</div>
            <div class="col quantity">Antall</div>
            <div class="col amount">Beløp</div>
            <div v-if="editMode" class="col-2 text-right">
              <span>Slett</span>
            </div>
          </ListHeader>

          <ListItem v-if="baseRent">
            <div class="col">Husleie</div>

            <div class="col percentage">
              <ValidationProvider v-if="editMode">
                <hl-Select
                  size="sm"
                  :hasMarginBottom="false"
                  :options="MVAOptions"
                  v-model="localBaseRent.vat"
                ></hl-Select>
              </ValidationProvider>
              <span v-else>{{ baseRent.vat }}%</span>
            </div>
            <div class="col quantity">
              {{ rentQuantity }}
            </div>
            <div class="col amount">
              <ValidationProvider v-if="editMode" name="Husleie" rules="required|numeric" v-slot="{ errors }">
                <hl-Input
                  size="sm"
                  type="number"
                  name="baseRent"
                  v-model="localBaseRent.rent"
                  :error="errors[0]"
                ></hl-Input>
              </ValidationProvider>
              <hl-Currency v-else :amount="parseFloat(baseRent.rent)"></hl-Currency>
            </div>

            <div v-if="editMode" class="col-2 text-right remove-row"></div>
          </ListItem>

          <div v-if="activeSubProductList.length">
            <ListItem v-for="(item, i) in activeSubProductList" :key="item.id">
              <div class="col">
                <div v-if="editMode">
                  <ValidationProvider :name="`Produkt ${i}`" rules="required" v-slot="{ errors }">
                    <hl-Input type="text" size="sm" v-model="item.name" :error="errors[0]" />
                  </ValidationProvider>
                </div>
                <div v-else>{{ item.name }}</div>
              </div>
              <div class="col percentage">
                <div v-if="editMode">
                  <ValidationProvider>
                    <hl-Select size="sm" :hasMarginBottom="false" :options="MVAOptions" v-model="item.vat"></hl-Select>
                  </ValidationProvider>
                </div>
                <div v-else-if="item.vat">{{ item.vat }}%</div>
              </div>
              <div class="col quantity">1</div>
              <div class="col amount">
                <div v-if="editMode">
                  <ValidationProvider :name="`Beløp ${i}`" :rules="'required|numeric'" v-slot="{ errors }">
                    <hl-Input size="sm" type="number" v-model="item.price" :error="errors[0]"></hl-Input>
                  </ValidationProvider>
                </div>
                <div v-else>
                  <hl-Currency :amount="parseFloat(item.price)"></hl-Currency>
                </div>
              </div>

              <div v-if="editMode" class="col-2 text-right remove-row">
                <i class="budicon-trash" @click="removeRow(item.id ? { id: item.id } : { localId: item.localId })"></i>
              </div>
            </ListItem>
          </div>

          <ListItem v-if="editMode" class="add-row mb-4 p-0">
            <div class="col text-center">
              <hl-Button class="w-100" outline variant="secondary" @click="addRow" :disabled="invalid">
                Legg til linje
              </hl-Button>
            </div>
          </ListItem>

          <ListHeader>
            <div class="col">
              <h6 v-if="baseRent.invoiceInterval" class="font-weight-normal">Sum eks. MVA*</h6>
            </div>
            <div class="col percentage">&nbsp;</div>
            <div class="col amount">
              <h6 class="font-weight-normal">
                <hl-Currency :amount="netTotal"></hl-Currency>
              </h6>
            </div>
          </ListHeader>

          <ListHeader>
            <div class="col">
              <h6 v-if="baseRent.invoiceInterval" class="font-weight-normal">MVA*</h6>
            </div>
            <div class="col percentage">&nbsp;</div>
            <div class="col amount">
              <h6 class="font-weight-normal">
                <hl-Currency :amount="totalTax"></hl-Currency>
              </h6>
            </div>
          </ListHeader>

          <hr />

          <ListHeader>
            <div class="col">
              <h6 v-if="baseRent.invoiceInterval">Sum å betale per husleieperiode</h6>
            </div>
            <div class="col percentage">&nbsp;</div>
            <div class="col amount">
              <h6>
                <hl-Currency :amount="totalWithTax"></hl-Currency>
              </h6>
            </div>
          </ListHeader>

          <p class="col m-0 mt-1">
            Husleien kan kun tillegges MVA hvis din utleievirksomhet er registert i merverdiavgiftsregisteret, og
            leietaker driver avgiftspliktig virksomhet.
          </p>
        </List>
      </div>
    </ValidationObserver>
  </BlockContainer>
  <!-- / Rent Information -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import BlockContainer from '@/components/BlockContainer';
import CancelButton from '@/components/UI/buttons/CancelButton';
import EditButton from '@/components/UI/buttons/EditButton';
import SaveButton from '@/components/UI/buttons/SaveButton';
import List from '@/components/UI/list/List';
import ListHeader from '@/components/UI/list/ListHeader';
import ListItem from '@/components/UI/list/ListItem';
import canLandlordChargeVAT from '@/mixins/tenancy/canLandlordChargeVAT';
import Button from '@modules/husleie-framework/src/components/buttons/Button.vue';
import Input from '@modules/husleie-framework/src/components/forms/inputs/input/Input.vue';
import i18nCommon from '@modules/i18n/mixins/Common';
import Currency from '@modules/husleie-framework/src/components/currency/Currency.vue';
import Select from '@modules/husleie-framework/src/components/forms/inputs/select/Select.vue';
import { TenancyApi } from '@modules/husleieapi2';

export default {
  mixins: [i18nCommon],
  components: {
    BlockContainer,
    CancelButton,
    EditButton,
    List,
    ListHeader,
    ListItem,
    SaveButton,
    'hl-Input': Input,
    'hl-Select': Select,
    'hl-Currency': Currency,
    'hl-Button': Button,
  },

  props: {
    landlordId: [String, Number],
    tenancyId: [String, Number],
  },

  data() {
    return {
      editMode: false,
      loading: {
        get: false,
        post: false,
      },
      subProductsToBeDeleted: [],
      localBaseRent: {},
      subProducts: [],
      localSubProducts: [],
    };
  },

  mixins: [canLandlordChargeVAT],

  computed: {
    ...mapGetters('tenancy', ['getTenancy', 'getTenancyRentInfoList', 'getTenancyPaymentInfo']),
    ...mapGetters(['isUserSudo']),

    activeSubProductList() {
      return this.editMode ? this.localSubProducts : this.subProducts;
    },

    rentQuantity() {
      return this.baseRent.invoiceInterval === 'month' ? 1 : 3;
    },

    baseRent() {
      const tenancy = this.getTenancy;
      if (tenancy == null || tenancy.rent == null) return null;
      return tenancy.rent;
    },

    netRent() {
      if (this.baseRent == null) return null;
      return this.baseRent.rent * this.rentQuantity;
    },

    subProductsNetTotal() {
      if (this.localSubProducts == null) return null;
      return this.localSubProducts.reduce((acc, item) => acc + parseFloat(item.price || 0), 0);
    },

    subProductsTotalTax() {
      if (this.localSubProducts == null) return null;
      return this.localSubProducts.reduce((acc, item) => acc + (parseFloat(item.price || 0) * item.vat) / 100, 0);
    },

    netTotal() {
      if (this.netRent == null || this.subProductsNetTotal == null) return null;
      return this.netRent + this.subProductsNetTotal;
    },

    totalTax() {
      if (this.netRent == null || this.baseRent == null || this.subProductsTotalTax == null) return null;
      return (this.netRent * this.baseRent.vat) / 100 + this.subProductsTotalTax;
    },

    totalWithTax() {
      if (this.netTotal == null || this.totalTax == null) return null;
      return this.netTotal + this.totalTax;
    },
    translationErrorCouldNotSaveRental() {
      return this.translate('error.could not save rental');
    },
  },

  methods: {
    ...mapActions('tenancy', [
      'fetchTenancyRent',
      'addTenancySubproduct',
      'updateTenancyRent',
      'updateTenancySubproduct',
      'deleteTenancySubproduct',
    ]),

    setEditModeAndLocalCopy() {
      if (this.getTenancy == null) return;
      this.$set(this, 'localBaseRent', JSON.parse(JSON.stringify(this.getTenancy.rent)));
      this.localSubProducts = JSON.parse(JSON.stringify(this.subProducts));
      this.subProductsToBeDeleted = [];
      this.editMode = true;
    },

    addRow() {
      this.localSubProducts.push({
        name: '',
        vat: 0,
        price: null,
        localId: this.localSubProducts.length,
      });
      this.$nextTick(() => {
        this.$refs.rentInfoForm.validate();
        console.log(this.$refs.rentInfoForm);
        this.$refs.rentInfoForm.fields.Produkt;
      });
    },

    removeRow(id) {
      console.log('removeRow', id);

      // we store the exisiting subproducts to be deleted
      if (id.id) {
        this.subProductsToBeDeleted.push(id.id);
        const index = this.localSubProducts.findIndex(row => {
          return row.id === id.id;
        });

        this.localSubProducts.splice(index, 1);
      }

      // we delete the local deleted subproducts. No need to store them, since they were never saved.
      if (id.localId) {
        const index = this.localSubProducts.findIndex(row => {
          return row.id === id.localId;
        });

        this.localSubProducts.splice(index, 1);
      }

      this.$nextTick(() => {
        this.$refs.rentInfoForm.validate();
      });
    },

    async fetchTenancySubProducts({ landlordId, tenancyId }) {
      if (landlordId == null || tenancyId == null) return;
      this.loading.get = true;
      try {
        const api = new TenancyApi();
        const res = await api.getLandlordTenancySubProducts(landlordId, tenancyId);
        return res;
      } catch (error) {
        console.error(error);
      }
      this.loading.get = false;
    },

    async fetchSubProducts() {
      try {
        this.subProducts = await this.fetchTenancySubProducts({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
        });
        this.localSubProducts = JSON.parse(JSON.stringify(this.subProducts));
      } catch (err) {
        console.error(err);
      }
    },

    async updateBaseRent() {
      try {
        await this.updateTenancyRent({
          landlordId: this.landlordId,
          tenancyId: this.tenancyId,
          data: this.localBaseRent,
        });
      } catch (error) {
        console.error(error);
        this.$toast(this.translationErrorCouldNotSaveRental, 'error');
      }
    },

    async addSubproducts() {
      const newSubproducts = this.localSubProducts.filter(sp => !sp.id);
      try {
        if (newSubproducts.length) {
          for (const subProduct of newSubproducts) {
            subProduct.invoiceInterval = 'month';
            subProduct.category = 2;
            await this.addTenancySubproduct({
              landlordId: this.landlordId,
              tenancyId: this.tenancyId,
              data: subProduct,
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre produkt.', 'error');
      }
    },

    async updateSubproducts() {
      const subProductsToBeUpdated = this.localSubProducts.filter(sp => sp.id);
      try {
        console.log('update subproducts');
        if (subProductsToBeUpdated.length) {
          for (const subProduct of subProductsToBeUpdated) {
            await this.updateTenancySubproduct({
              landlordId: this.landlordId,
              tenancyId: this.tenancyId,
              subProductId: subProduct.id,
              data: subProduct,
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke lagre produkt.', 'error');
      }
    },

    async deleteSubproducts() {
      try {
        if (this.subProductsToBeDeleted.length) {
          for (const subProduct of this.subProductsToBeDeleted) {
            await this.deleteTenancySubproduct({
              landlordId: this.landlordId,
              tenancyId: this.tenancyId,
              subProductId: subProduct,
            });
          }
        }
      } catch (error) {
        console.error(error);
        this.$toast('Kunne ikke slette produkt.', 'error');
      }
    },

    async saveAndCloseEdit() {
      this.loading.post = true;
      try {
        await Promise.all([
          this.updateBaseRent(),
          this.addSubproducts(),
          this.updateSubproducts(),
          this.deleteSubproducts(),
        ]);
        await this.fetchSubProducts();
        this.editMode = false;
      } catch (error) {
        console.error(error);
        this.$toast('Noe gikk galt.', 'error');
      }
      this.loading.post = false;
    },
  },

  mounted() {
    this.fetchSubProducts();
  },
};
</script>

<style scoped lang="scss">
.amount {
  flex: 0 0 160px;
  text-align: right;
}

.percentage {
  flex: 0 0 120px;
}

.quantity {
  flex: 0 0 80px;
  text-align: center;
}

.add-row {
  background: $gray-100;
  color: $gray-800;
  box-shadow: $box-shadow-1;
}

.remove-row {
  i {
    font-size: 1rem;
  }
}

.sudo {
  border: 2px solid #ed2135;
}
</style>
